import type { AgencyPortalUser } from "../types";

/**
 * Staff shadow accounts (M25-B1..B3). Every agency carries a permanent
 * `staff_alias_suffix`; each staff member with `sales.studio_shadow_access`
 * gets a hidden member login `<name>+<suffix>@echoflicks.com` in it
 * (supabase/migrations/20260925170000_agency_staff_shadow_accounts.sql).
 */
export const STAFF_EMAIL_DOMAIN = "echoflicks.com";

/** "<name>+vdb@echoflicks.com": what staff type to sign in to this agency. */
export const staffSignInPattern = (suffix: string): string =>
  `<name>+${suffix}@${STAFF_EMAIL_DOMAIN}`;

/**
 * Echoflicks staff login inside a tenant (the portal's Team page hides it).
 * A row read before the column exists counts as a client login.
 */
export const isStaffPortalUser = (user: Pick<AgencyPortalUser, "is_staff">) =>
  user.is_staff === true;

/** Created (and removed) automatically for a staff member. */
export const isShadowPortalUser = (
  user: Pick<AgencyPortalUser, "shadow_of_sales_id">,
) => user.shadow_of_sales_id != null;

/**
 * The agency's live staff sign-in e-mails: its logins shaped
 * `<name>+<suffix>@echoflicks.com`, lowercased and sorted.
 */
export const staffSignInEmails = (
  users: readonly Pick<AgencyPortalUser, "email">[],
  suffix: string | null | undefined,
): string[] => {
  if (!suffix) {
    return [];
  }
  const tail = `+${suffix}@${STAFF_EMAIL_DOMAIN}`;
  return users
    .map((user) => user.email.trim().toLowerCase())
    .filter((email) => email.endsWith(tail) && email.length > tail.length)
    .sort();
};

const ADDRESS_RE = /^(.+)@([^@]+)$/;

/**
 * B4.1: an Echoflicks staff address. Mirrors `public.is_staff_portal_email`
 * (supabase/migrations/20260926120000_agency_internal_auto.sql), which the
 * database applies to every new or re-addressed portal login: anything on
 * @echoflicks.com, or an enabled CRM staff (sales) address once any +tag is
 * removed from both sides (jonny.biggins+vdb@gmail.com matches the roster's
 * jonny.biggins@gmail.com). Used by the invite dialog to say so up front.
 */
export const isStaffEmail = (
  email: string | null | undefined,
  roster: readonly { email?: string | null; disabled?: boolean | null }[],
): boolean => {
  const match = ADDRESS_RE.exec((email ?? "").trim().toLowerCase());
  if (!match) return false;
  const [, rawLocal, domain] = match;
  if (domain === STAFF_EMAIL_DOMAIN) return true;
  const local = rawLocal.split("+")[0];
  if (!local) return false;
  return roster.some((staff) => {
    if (staff.disabled === true) return false;
    const staffMatch = ADDRESS_RE.exec(
      (staff.email ?? "").trim().toLowerCase(),
    );
    return (
      staffMatch != null &&
      staffMatch[2] === domain &&
      staffMatch[1].split("+")[0] === local
    );
  });
};

/** Client logins first, staff logins after; order is kept within each group. */
export const clientUsersFirst = <T extends Pick<AgencyPortalUser, "is_staff">>(
  users: readonly T[],
): T[] =>
  [...users].sort(
    (a, b) => Number(isStaffPortalUser(a)) - Number(isStaffPortalUser(b)),
  );
